import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

import useTriage from "../../data/useTriage";
import useVehicleSingle from "../../data/useVehicleSingle";
import { useAuth } from "../../contexts/AuthContext";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import TriageTimerCard from "../../components/triage/TriageTimerCard";
import LoadingWheel from "../../components/shared/LoadingWheel";
import ContactFromId from "../../components/contacts/ContactFromId";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Card from "../../components/shared/Card/Card";
import TriageButton from "../../components/triage/TriageButton";
import DepollutionVehicleSummary from "../../components/depollution/DepollutionVehicleSummary";
import DepollutionPartsEditor from "../../components/depollution/DepollutionPartsEditor";
import CreateJobs from "../../components/jobs/CreateJobs";

export default function Triage({ triageId: triageIdProp, vehicleId, collapsible = false }: { triageId?: string; vehicleId?: string; collapsible?: boolean; }) {
  const { triageId: triageIdParam } = useParams();
  const navigate = useNavigate();
  const isStandalonePage = triageIdProp === undefined && vehicleId === undefined;
  const triageLabel = "Triage";
  // On the standalone page the timer starts automatically once the vehicle's
  // triage has loaded (scan, search or row click alike).
  const autoStartTimer = isStandalonePage;

  const { setInfo } = usePageNameContext();
  const { userId } = useAuth();
  const isTme = CheckFeatureFlag("TME");
  const { saveHandlers } = useDefaultCRUDHandlers(`${triageLabel} item`);

  const { vehicle: vehicleQuery } = useVehicleSingle(vehicleId);
  const vehicleTriageId = vehicleQuery.data?.data?.triageId;
  const resolvedTriageId = triageIdProp ?? (vehicleId ? vehicleTriageId ?? undefined : triageIdParam);

  const { triage, triageItems, triageSteps, saveTriageItem } = useTriage(resolvedTriageId);
  const { vehicle: triageVehicleQuery } = useVehicleSingle(triage.data?.data?.vehicleId);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savingStepId, setSavingStepId] = useState<string | null>(null);

  const steps = useMemo(
    () => [...(triageSteps.data?.data ?? [])].sort((a, b) => a.order - b.order),
    [triageSteps.data?.data]
  );
  const items = triageItems.data?.data ?? [];
  const vehicle = triage.data?.data?.vehicle;
  const fullVehicle = triageVehicleQuery.data?.data ?? vehicleQuery.data?.data ?? vehicle;

  useEffect(() => {
    if (!isStandalonePage || triage.isLoading) return;

    if (!triage.data?.data) {
      setInfo({
        name: `${triageLabel} not found`,
        desc: `The ${triageLabel.toLowerCase()} record you are looking for does not exist`,
      });
      return;
    }

    setInfo({
      name: `${triageLabel} - ${vehicle?.vrm ?? "Vehicle"}`,
      desc: `${vehicle?.combinedMake ?? ""} ${vehicle?.combinedModel ?? ""}`.trim(),
    });
  }, [isStandalonePage, triage.isLoading, triage.data?.data?.id, vehicle?.vrm, vehicle?.combinedMake, vehicle?.combinedModel, setInfo, triageLabel]);

  useEffect(() => {
    if (!triageItems.data?.data) return;

    const nextValues: Record<string, string> = {};
    for (const item of triageItems.data.data) {
      nextValues[item.triageStepId] = item.value ?? "";
    }
    setDraftValues(nextValues);
  }, [triageItems.data?.data]);

  const rowData = useMemo(() => {
    return steps.map((step) => {
      const item = items.find((x) => x.triageStepId === step.id);
      return {
        step,
        item,
        isCompleted: !!item?.completed,
      };
    });
  }, [steps, items]);

  const completedCount = rowData.filter((x) => x.isCompleted).length;
  const totalCount = rowData.length;
  const allCompleted = totalCount > 0 && completedCount === totalCount;

  const parseOptions = (options: string | null) => {
    if (!options) return [];
    return options
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  };

  // True when marking this step complete finishes the last outstanding item.
  // Completing the last step closes the triage session server-side, and on the
  // standalone page with TME enabled we mirror the scan-to-finish flow by
  // returning to the list ready for the next vehicle. Tied to the save action
  // so it never fires when simply viewing an already-completed triage.
  const completesTriage = (triageStepId: string, isCompleted: boolean) =>
    isCompleted &&
    rowData.length > 0 &&
    rowData.every((row) => row.step.id === triageStepId || row.isCompleted);

  const buildSaveHandlers = (triageStepId: string, isCompleted: boolean) => {
    const shouldRedirect = isStandalonePage && isTme && completesTriage(triageStepId, isCompleted);
    return {
      onSuccess: (...args: unknown[]) => {
        (saveHandlers.onSuccess as ((...a: unknown[]) => void) | undefined)?.(...args);
        if (shouldRedirect) navigate("/triage");
      },
      onError: saveHandlers.onError,
      onSettled: () => setSavingStepId(null),
    };
  };

  const saveStep = (triageStepId: string, isCompleted: boolean) => {
    const vehicleId = triage.data?.data?.vehicleId;
    if (!vehicleId) return;

    setSavingStepId(triageStepId);
    saveTriageItem.mutate(
      {
        vehicleId,
        triageStepId,
        value: draftValues[triageStepId] ?? "",
        isCompleted,
      },
      buildSaveHandlers(triageStepId, isCompleted)
    );
  };

  const saveStepValue = (triageStepId: string, value: string, isCompleted: boolean) => {
    const vehicleId = triage.data?.data?.vehicleId;
    if (!vehicleId) return;

    setDraftValues((prev) => ({ ...prev, [triageStepId]: value }));
    setSavingStepId(triageStepId);
    saveTriageItem.mutate(
      {
        vehicleId,
        triageStepId,
        value,
        isCompleted,
      },
      buildSaveHandlers(triageStepId, isCompleted)
    );
  };

  const isTriageDataLoading = !!resolvedTriageId && (triage.isLoading || triageItems.isLoading);
  if ((vehicleId && !triageIdProp && vehicleQuery.isLoading) || isTriageDataLoading || triageSteps.isLoading) {
    return (
      <Card title={triageLabel} collapsible={false}>
        <div className="p-4">
          <LoadingWheel />
        </div>
      </Card>
    );
  }

  if (vehicleId && !triage.data?.data) {
    return (
      <Card title={triageLabel} collapsible={false}>
        <div className="p-4">
          <TriageButton
            vehicleId={vehicleId}
            triageId={vehicleTriageId}
            openInNewTab={false}
            navigateOnCreate={false}
          />
        </div>
      </Card>
    );
  }

  if (!triage.data?.data) return null;

  // In the TME side-by-side layout, stretch both cards (and the Card's inner
  // panel) to the row height so their bottoms line up.
  const stretchCardClass = isTme && isStandalonePage && fullVehicle ? "h-full [&>div]:h-full" : undefined;

  return (
    <div className="flex flex-col gap-4">

      {/* TME puts the timer beside the vehicle info so both are visible without scrolling. */}
      <div className={isTme && isStandalonePage && fullVehicle ? "grid grid-cols-1 gap-4 xl:grid-cols-2" : "flex flex-col gap-4"}>
        {isStandalonePage && fullVehicle && (
          <Card containerClassName={stretchCardClass}>
            <DepollutionVehicleSummary
              vehicle={fullVehicle}
              completedCount={completedCount}
              totalCount={totalCount}
              depollutionLabel={triageLabel}
              hideVehicleActions={isTme}
            />
          </Card>
        )}

        <TriageTimerCard
          triageId={resolvedTriageId}
          userId={userId}
          autoStart={autoStartTimer}
          enableScanToggle={isStandalonePage}
          onSessionFinished={isStandalonePage ? () => navigate("/triage") : undefined}
          containerClassName={stretchCardClass}
        />
      </div>

      <Card
        title={`${triageLabel} ${completedCount}/${totalCount} ${completedCount === totalCount ? "✔" : ""}`}
        collapsible={collapsible}
        defaultOpen={collapsible ? !allCompleted : true}
        titleClassName={collapsible && completedCount === totalCount ? "bg-green-300" : ""}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Value</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Completed By</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rowData.map(({ step, item, isCompleted }) => (
              <tr key={step.id} className="bg-white hover:bg-gray-50">
                <td className="px-4 py-2 text-sm">
                  <div>{step.name}</div>
                  {step.subtitle && (
                    <div className="text-xs text-gray-500">{step.subtitle}</div>
                  )}
                </td>
                <td className="px-4 py-2 text-sm">
                  {step.needsValue ? (
                    (() => {
                      const parsedOptions = parseOptions(step.options);
                      if (parsedOptions.length > 0) {
                        if (parsedOptions.length <= 3) {
                          const selectedValue = draftValues[step.id] ?? "";
                          return (
                            <div className="flex flex-wrap gap-2">
                              {parsedOptions.map((opt) => {
                                const selected = selectedValue === opt;
                                return (
                                  <button
                                    key={opt}
                                    type="button"
                                    disabled={savingStepId === step.id}
                                    onClick={() => saveStepValue(step.id, opt, true)}
                                    className={`rounded-md border px-3 py-1.5 text-sm font-medium shadow-sm ${
                                      selected
                                        ? "border-primary-600 bg-primary-600 text-white hover:bg-primary-700"
                                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                                  >
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        }

                        return (
                          <select
                            value={draftValues[step.id] ?? ""}
                            disabled={savingStepId === step.id}
                            onChange={(e) => saveStepValue(step.id, e.target.value, true)}
                            className="w-full max-w-xs rounded-md border border-gray-300 px-2 py-1 text-sm"
                          >
                            <option value="">Select...</option>
                            {parsedOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        );
                      }

                      return (
                        <div className="flex items-center gap-2">
                          <input
                            value={draftValues[step.id] ?? ""}
                            onChange={(e) =>
                              setDraftValues((prev) => ({ ...prev, [step.id]: e.target.value }))
                            }
                            className="w-full max-w-xs rounded-md border border-gray-300 px-2 py-1 text-sm"
                            placeholder="Enter value"
                          />
                          <ButtonInput
                            label="Save"
                            isSubmit={false}
                            classes=""
                            isLoading={savingStepId === step.id}
                            onClick={() => saveStep(step.id, isCompleted)}
                          />
                        </div>
                      );
                    })()
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {item?.completedBy ? <ContactFromId contactId={item.completedBy} /> : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-2 text-center">
                  <ButtonInput
                    isSubmit={false}
                    onClick={() => saveStep(step.id, !isCompleted)}
                    isLoading={savingStepId === step.id}
                    classes={undefined}
                  >
                    <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 shadow-sm hover:bg-gray-50">
                      <CheckCircleIcon className={`h-5 w-5 ${isCompleted ? "text-green-500" : "text-gray-300"}`} />
                      {isCompleted ? "Completed" : "Mark Complete"}
                    </div>
                  </ButtonInput>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {isStandalonePage && fullVehicle && (
        <DepollutionPartsEditor vehicle={fullVehicle} allowMarkDamaged nonRecyclingOnly={isTme} />
      )}

      {/* {isStandalonePage && fullVehicle && (
        <CreateJobs lead={null} vehicle={fullVehicle} hidePartsInStock />
      )} */}
    </div>
  );
}
